<template>
  <v-row no-gutters>
    <v-col>
      <main-loader v-if="!isFetched && !disabled" />
      <admin-wrapper
        :inputs="inputs"
        @save="setEmailSettings"
        :isCustomSaving="isConfirmButtonLoading"
        :disabled="disabled"
        v-if="!isFetching"
      >
        <template #emails.input>
          <v-col cols="8">
            <data-input
              @drop="handleNoDrop"
              @dragend="handleNoDrop"
              @dragenter="handleNoDrop"
              :dataType="dataTypeCombo"
              v-model="emailSettings.email_recipientsArray"
              :disabled="disabled"
              append-icon=""
              multiple
              outlined
              deletable-chips
              dense
              chips
            >
            </data-input>
          </v-col>
        </template>
        <template #title.input>
          <v-row no-gutters>
            <v-col>
              <v-row no-gutters>
                <v-col class="emailTemplateTitle pa-0" :class="disabled ? 'text--disabled' : ''">
                  {{ $i18n.t('admin.failureStatusEmailTemplateTitle') }}
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col class="text-caption font-italic font-weight-light pa-0 text-left">
                  {{ $i18n.t('admin.failureStatusStatusEmailTemplateSubtitle') }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </template>
        <template #failureAvailableTags.input>
          <v-chip v-for="tag in tags" :key="tag" class="mr-2" draggable @dragstart="drag" :disabled="disabled" small>
            {{ `{${tag}\}` }}
          </v-chip>
        </template>
        <template #failureEmailTitle.input>
          <v-col cols="8">
            <data-input
              clearable
              :dataType="dataType"
              v-model="emailSettings.email_title"
              :disabled="disabled"
              dense
              outlined
            ></data-input>
          </v-col>
        </template>
        <template #failureEmailContent.input>
          <v-col cols="8">
            <tiptap-vuetify
              :disabled="disabled"
              v-model="emailSettings.email_content"
              :extensions="extensions"
              :placeholder="$i18n.t('admin.emailPlaceholder')"
              :card-props="{ flat: true, outlined: true }"
              style="text-align: left"
            />
          </v-col>
        </template>
      </admin-wrapper>
    </v-col>
  </v-row>
</template>

<script>
import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';
import {
  TiptapVuetify,
  Bold,
  Italic,
  Underline,
  Blockquote,
  Paragraph,
  Heading,
  History,
  HardBreak,
} from 'tiptap-vuetify';
import { call } from 'vuex-pathify';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'FailureEmailStatusTab',
  components: { MainLoader, AdminWrapper, DataInput, TiptapVuetify },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    tags: ['osoba_zgłaszająca', 'data_zgłoszenia', 'opis_skrocony', 'opis_pelny', 'nr_zgloszenia'],
    emailSettings: {},
    dataType: { name: 'text' },
    dataTypeCombo: { name: 'combobox' },
    extensions: [
      Bold,
      Italic,
      Underline,
      Blockquote,
      Paragraph,
      [
        Heading,
        {
          options: {
            levels: [1, 2, 3],
          },
        },
      ],
      HardBreak,
      History,
    ],
    isFetched: false,
    isFetching: false,
    isConfirmButtonLoading: false,
  }),
  computed: {
    inputs() {
      return [
        {
          name: 'emails',
          translationPath: 'admin.emailAddressTitle',
          subtitle: 'admin.emailAddressStatusSubtitle',
          align: 'start',
          isCustom: true,
        },
        {
          name: 'title',
          translationPath: 'admin.failureAvailableTags',
          hasSuffix: false,
          isCustom: true,
          isNamedSlot: false,
          outlined: true,
        },
        {
          name: 'failureAvailableTags',
          translationPath: 'admin.failureAvailableTags',
        },
        {
          name: 'failureEmailTitle',
          translationPath: 'admin.failureEmailTitle',
          align: 'start',
          isCustom: true,
        },
        {
          name: 'failureEmailContent',
          translationPath: 'admin.failureEmailContent',
          isCustom: true,
          align: 'start',
        },
      ];
    },
  },
  methods: {
    getEmailSettings: call('zdm/getReportChangeEmailSettings'),
    editEmailSettings: call('zdm/editReportChangeEmailSettings'),
    drag(event) {
      event.dataTransfer.setData('text', event.target.firstChild.innerText);
    },
    async setEmailSettings() {
      this.isConfirmButtonLoading = true;
      const email_recipients = this.emailSettings.email_recipientsArray.join(', ');
      const payload = {
        email_content: this.emailSettings.email_content,
        email_title: this.emailSettings.email_title,
        email_recipients,
      };
      try {
        await this.editEmailSettings(payload);
        this.isConfirmButtonLoading = false;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.setEmailSettings'),
        });
      } catch (e) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.setEmailSettingsFailed'),
        });
        this.emailSettings = await this.getEmailSettings();
        this.isConfirmButtonLoading = false;
      }
    },
    handleNoDrop(e) {
      e.preventDefault();
      return false;
    },
    async init() {
      this.isFetched = false;
      if (this.disabled) {
        this.isFetched = true;
      } else {
        await this.fetchData();
      }
    },
    async fetchData() {
      this.isFetched = false;
      this.isFetching = true;
      const settings = await this.getEmailSettings();
      if (settings.email_recipients === '' || settings.email_recipients === null) {
        settings.email_recipientsArray = [];
      } else {
        settings.email_recipientsArray = settings.email_recipients.split(', ');
      }
      delete settings.email_recipients;
      this.emailSettings = settings;
      this.isFetching = false;
      this.isFetched = true;
    },
  },
  watch: {
    disabled(nV) {
      if (!nV) {
        this.fetchData();
      }
    },
  },
  created() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
.emailTemplateTitle {
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: left;
}
::v-deep {
  .v-select__selections {
    flex-wrap: wrap !important;
  }
  .v-select.v-input--dense .v-chip {
    margin: 2px 4px;
  }
}
</style>
